import React, { useState, useEffect, useRef } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { FileText, Plus, Trash2, Clock, Paperclip, Download, X, File } from 'lucide-react';

const NotesPage = () => {
  const { api, user } = useAuth();
  const { t, tError, formatDate } = useLanguage();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => { fetchNotes(); }, []);

  const fetchNotes = async () => {
    try {
      const res = await api.get('/notes');
      setNotes(res.data || []);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) return;
    setSaving(true);
    try {
      await api.post('/notes', { title: title.trim(), content: content.trim() });
      setTitle('');
      setContent('');
      await fetchNotes();
    } catch (err) {
      alert(tError(err.response?.data?.detail) || t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('notes.deleteConfirm'))) return;
    try {
      await api.delete(`/notes/${id}`);
      await fetchNotes();
    } catch (err) {
      alert(tError(err.response?.data?.detail) || t('common.error'));
    }
  };

  const handleFileUpload = async (noteId, files) => {
    if (!files || files.length === 0) return;
    setUploading(noteId);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        await api.post(`/notes/${noteId}/attachments`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      await fetchNotes();
    } catch (err) {
      alert(tError(err.response?.data?.detail) || t('notes.uploadError'));
    } finally {
      setUploading(null);
    }
  };

  const handleDownload = async (noteId, fileId, filename) => {
    try {
      const response = await api.get(`/notes/${noteId}/attachments/${fileId}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(t('notes.downloadError'));
    }
  };

  const handleDeleteAttachment = async (noteId, fileId) => {
    if (!window.confirm(t('notes.confirmDeleteFile'))) return;
    try {
      await api.delete(`/notes/${noteId}/attachments/${fileId}`);
      await fetchNotes();
    } catch (err) {
      alert(t('common.error'));
    }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return t('notes.sizeBytes', { size: bytes });
    if (bytes < 1048576) return t('notes.sizeKb', { size: (bytes / 1024).toFixed(1) });
    return t('notes.sizeMb', { size: (bytes / 1048576).toFixed(1) });
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto fade-in" data-testid="notes-page">
        <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500 mb-4" data-testid="notes-title">
          {t('notes.title')}
        </h2>

        {/* Add Note Form */}
        <div className="glass p-4 mb-6" data-testid="notes-form">
          <form onSubmit={handleSubmit} className="space-y-3">
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('notes.title')}
              className="w-full px-3 py-2 rounded-xl bg-black/30 border border-white/10 text-white text-sm"
              data-testid="notes-title-input"
            />
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={t('common.description')}
              rows={3}
              className="w-full px-3 py-2 rounded-xl bg-black/30 border border-white/10 text-white text-sm resize-none"
              data-testid="notes-content-input"
            />
            <button
              type="submit"
              disabled={saving || !title.trim() || !content.trim()}
              className="px-4 py-2 rounded-xl bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 font-bold flex items-center gap-2 text-sm"
              data-testid="notes-submit"
            >
              <Plus className="w-4 h-4" />
              {t('common.save')}
            </button>
          </form>
        </div>

        {/* Notes List */}
        {loading ? (
          <div className="flex justify-center py-12"><div className="loading-spinner"></div></div>
        ) : notes.length === 0 ? (
          <div className="text-center py-12 text-gray-400" data-testid="notes-empty">
            <FileText className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <p>{t('notes.noNotes')}</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="notes-list">
            {notes.map((note) => (
              <div key={note.id} className="glass p-4" data-testid={`note-${note.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-white mb-1" data-testid={`note-title-${note.id}`}>{note.title}</h3>
                    <p className="text-sm text-gray-300 whitespace-pre-wrap mb-2" data-testid={`note-content-${note.id}`}>{note.content}</p>
                    
                    {/* Attachments */}
                    {note.attachments && note.attachments.length > 0 && (
                      <div className="mt-3 space-y-1.5" data-testid={`note-attachments-${note.id}`}>
                        {note.attachments.map((att) => (
                          <div key={att.id} className="flex items-center gap-2 p-2 rounded-lg bg-white/5 border border-white/10">
                            <File className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                            <span className="text-sm text-gray-300 truncate flex-1">{att.filename}</span>
                            <span className="text-xs text-gray-500">{formatSize(att.size)}</span>
                            <button
                              onClick={() => handleDownload(note.id, att.id, att.filename)}
                              className="p-1 rounded hover:bg-white/10 text-cyan-400"
                              title={t('common.download')}
                              data-testid={`note-download-${att.id}`}
                            >
                              <Download className="w-3.5 h-3.5" />
                            </button>
                            {user?.role === 'admin' && (
                              <button
                                onClick={() => handleDeleteAttachment(note.id, att.id)}
                                className="p-1 rounded hover:bg-red-500/20 text-red-400"
                                title={t('common.delete')}
                                data-testid={`note-delete-attachment-${att.id}`}
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="flex items-center gap-3 mt-2">
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <Clock className="w-3 h-3" />
                        <span>{t('notes.addedBy', { author: note.author, date: note.created_at ? formatDate(note.created_at, { dateStyle: 'short', timeStyle: 'short' }) : '' })}</span>
                      </div>
                      <label className="flex items-center gap-1 text-xs text-cyan-400 cursor-pointer hover:text-cyan-300">
                        <Paperclip className="w-3 h-3" />
                        <span>{uploading === note.id ? t('notes.uploading') : t('notes.attach')}</span>
                        <input
                          type="file"
                          multiple
                          className="hidden"
                          onChange={(e) => handleFileUpload(note.id, e.target.files)}
                          disabled={uploading === note.id}
                          data-testid={`note-upload-${note.id}`}
                        />
                      </label>
                    </div>
                  </div>
                  {user?.role === 'admin' && (
                    <button
                      onClick={() => handleDelete(note.id)}
                      className="p-2 rounded-xl bg-red-500/10 text-red-400 hover:bg-red-500/20 ml-3 flex-shrink-0"
                      data-testid={`note-delete-${note.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default NotesPage;
